/**
 * Icons.
 *
 * Hand-drawn inline SVG rather than an icon package. A dependency would ship a thousand glyphs
 * to send fourteen, and these are all the same 24-grid, 1.75 stroke, rounded caps — which is
 * what actually makes an icon set look like a set.
 *
 * `currentColor` throughout, so an icon inherits the colour of the nav item it sits in.
 */

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconDashboard = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </Icon>
);

export const IconAlert = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3.5 2.8 19.5h18.4L12 3.5Z" />
    <path d="M12 10v4" />
    <path d="M12 17.2h.01" />
  </Icon>
);

export const IconBell = (props: IconProps) => (
  <Icon {...props}>
    <path d="M18 8.5a6 6 0 1 0-12 0c0 6-2 7.5-2 7.5h16s-2-1.5-2-7.5Z" />
    <path d="M13.7 20a2 2 0 0 1-3.4 0" />
  </Icon>
);

export const IconBuilding = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15" />
    <path d="M14 10h4a2 2 0 0 1 2 2v9" />
    <path d="M2 21h20" />
    <path d="M7.5 8h3M7.5 12h3M7.5 16h3M17 14h.01M17 17.5h.01" />
  </Icon>
);

export const IconDoor = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 21V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21" />
    <path d="M3.5 21h17" />
    <path d="M14.5 12.5h.01" />
  </Icon>
);

export const IconContract = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
    <path d="M8.5 13h7M8.5 16.5h4" />
  </Icon>
);

/**
 * A signed sheet: a page with a hand-written stroke across the rule at the bottom of it.
 *
 * Deliberately not the same drawing as `IconContract`, which is a document with lines of text on
 * it and stands for a tenancy. This one stands for a document somebody has put their name to, and
 * the two appear next to each other in the rail — a mark that has to be told apart from its
 * neighbour at 18 pixels has to differ in its silhouette, not in a detail.
 */
export const IconSignature = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
    <path d="M14 3v5h5" />
    <path d="M8.5 17.2c1.7 0 2.1-1 2.4-2.4.3-1.5.7-3.4 1.9-3.4 1 0 1 1.2.6 2.4-.5 1.4-1.1 2.2-.3 2.6.7.4 1.7-.3 2.3-1" />
    <path d="M15.5 19.5h4.7" />
  </Icon>
);

export const IconUsers = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
    <path d="M16.5 5.4a3.2 3.2 0 0 1 0 5.9" />
    <path d="M18 14.9c2 .7 3.4 2.4 3.4 5.1" />
  </Icon>
);

export const IconInvoice = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 2.5h12a1 1 0 0 1 1 1V21l-3-1.6L13 21l-3-1.6L7 21l-3-1.6V3.5a1 1 0 0 1 1-1Z" />
    <path d="M8.5 8h7M8.5 12h7M8.5 15.5h4" />
  </Icon>
);

export const IconWallet = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a2 2 0 0 1 2 2v1" />
    <rect x="3" y="7.5" width="18" height="12" rx="2.5" />
    <path d="M16.5 13.5h.01" />
  </Icon>
);

export const IconCard = (props: IconProps) => (
  <Icon {...props}>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M2.5 9.5h19M6 15h3.5" />
  </Icon>
);

export const IconLifebuoy = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.4 9.6a2.7 2.7 0 1 1 3.4 3.4c-.5.2-.8.7-.8 1.3" />
    <path d="M12 17.2h.01" />
  </Icon>
);

export const IconReceipt = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 3.5h14v17l-2.3-1.4-2.4 1.4-2.3-1.4-2.4 1.4L7.3 19 5 20.5v-17Z" />
    <path d="M9 8.5h6M9 12.5h6" />
  </Icon>
);

export const IconChart = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.5 20.5h17" />
    <path d="M7 20.5v-7M12 20.5V6M17 20.5v-4.5" />
  </Icon>
);

export const IconWrench = (props: IconProps) => (
  <Icon {...props}>
    <path d="M15.6 3.6a5.5 5.5 0 0 0-6.9 6.9L3.6 15.6a2 2 0 0 0 0 2.8l2 2a2 2 0 0 0 2.8 0l5.1-5.1a5.5 5.5 0 0 0 6.9-6.9l-3.2 3.2-2.8-.7-.7-2.8 3.2-3.2Z" />
  </Icon>
);

export const IconSettings = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 14.5a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2v.2a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-3-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.2-3l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 2.9-1.2V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 3 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9h.2a2 2 0 0 1 0 4H21a1.7 1.7 0 0 0-1.6 1.2Z" />
  </Icon>
);

export const IconSend = (props: IconProps) => (
  <Icon {...props}>
    <path d="M21 3 10.5 13.5" />
    <path d="M21 3l-6.8 18-3.7-7.5L3 9.8 21 3Z" />
  </Icon>
);

export const IconMail = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </Icon>
);

export const IconPlus = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const IconSearch = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </Icon>
);

export const IconChevronDown = (props: IconProps) => (
  <Icon {...props}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);

export const IconLogout = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </Icon>
);

export const IconMenu = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
);

export const IconClose = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);

export const IconCheck = (props: IconProps) => (
  <Icon {...props}>
    <path d="m5 12.5 5 5L19 7" />
  </Icon>
);

/**
 * The two states of module sharing, drawn as a pair.
 *
 * They are only legible against each other, so they are one shape with one difference: a chain
 * whose links meet, and the same chain broken. Anything else — a padlock, two overlapping
 * circles — says "locked" or "sets" rather than "these two are joined, or they are not".
 */
export const IconLink = (props: IconProps) => (
  <Icon {...props}>
    <path d="M10 13.5a3.5 3.5 0 0 0 5 0l3-3a3.54 3.54 0 0 0-5-5l-1.5 1.5" />
    <path d="M14 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.54 3.54 0 0 0 5 5l1.5-1.5" />
  </Icon>
);

export const IconSplit = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9.5 14.5a3.5 3.5 0 0 0 4-.5l1-1" />
    <path d="M14.5 9.5a3.5 3.5 0 0 0-4 .5l-1 1" />
    <path d="M17 4.5 19.5 2M20 8h3M16 1v3" />
    <path d="M7 19.5 4.5 22M4 16H1M8 23v-3" />
  </Icon>
);

export const IconCopy = (props: IconProps) => (
  <Icon {...props}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
  </Icon>
);

export const IconArrowLeft = (props: IconProps) => (
  <Icon {...props}>
    <path d="M19 12H5" />
    <path d="M11 6l-6 6 6 6" />
  </Icon>
);

export const IconPencil = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 20h4l10.5-10.5a2.8 2.8 0 0 0-4-4L4 16v4Z" />
    <path d="M14.5 5.5l4 4" />
  </Icon>
);

export const IconTrash = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 7h16" />
    <path d="M10 11v6M14 11v6" />
    <path d="M5.5 7l1 12.5a1.5 1.5 0 0 0 1.5 1.4h8a1.5 1.5 0 0 0 1.5-1.4L19 7" />
    <path d="M9 7V4.5A1.5 1.5 0 0 1 10.5 3h3A1.5 1.5 0 0 1 15 4.5V7" />
  </Icon>
);

export const IconDrag = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="9" cy="6" r="1.2" fill="currentColor" />
    <circle cx="15" cy="6" r="1.2" fill="currentColor" />
    <circle cx="9" cy="12" r="1.2" fill="currentColor" />
    <circle cx="15" cy="12" r="1.2" fill="currentColor" />
    <circle cx="9" cy="18" r="1.2" fill="currentColor" />
    <circle cx="15" cy="18" r="1.2" fill="currentColor" />
  </Icon>
);

/** Staff. A person on an identity card — distinct from IconUsers, which is the client list. */
export const IconTeam = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
    <path d="M9 4.5V3h6v1.5" />
    <circle cx="12" cy="10.5" r="2.4" />
    <path d="M7.8 16.4a4.6 4.6 0 0 1 8.4 0" />
  </Icon>
);

export const IconBank = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 9.5 12 4l9 5.5" />
    <path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" />
    <path d="M3 21h18" />
  </Icon>
);

/** An open eye — "the password is showing". */
/** Leaves the application: an arrow out of a box. Used wherever a link opens another site. */
export const IconExternal = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 4h6v6" />
    <path d="M20 4 11 13" />
    <path d="M18 14.5V19a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 19V7.5A1.5 1.5 0 0 1 5 6h4.6" />
  </Icon>
);

export const IconEye = (props: IconProps) => (
  <Icon {...props}>
    <path d="M2.4 12S6 5.6 12 5.6 21.6 12 21.6 12 18 18.4 12 18.4 2.4 12 2.4 12z" />
    <circle cx="12" cy="12" r="3.1" />
  </Icon>
);

/** The same eye, struck through — "the password is hidden". */
export const IconEyeOff = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9.6 5.9A9.6 9.6 0 0 1 12 5.6c6 0 9.6 6.4 9.6 6.4a17 17 0 0 1-2.9 3.6" />
    <path d="M6.2 7.4A16.6 16.6 0 0 0 2.4 12S6 18.4 12 18.4a9.4 9.4 0 0 0 3.9-.8" />
    <path d="M9.9 9.9a3.1 3.1 0 0 0 4.3 4.3" />
    <path d="M3.5 3.5l17 17" />
  </Icon>
);

/**
 * A drawing pin, seen from the side.
 *
 * Not a star and not a flag. A star reads as a rating and a flag reads as a problem; a pin says
 * exactly one thing — this one is held where I can see it — and says it without implying anything
 * about the property itself.
 */
export const IconPushpin = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 4h6l-.8 5.4 3 2.6H6.8l3-2.6z" />
    <path d="M12 12v8" />
  </Icon>
);

/**
 * A remittance: money leaving on its way to somebody else.
 *
 * A banknote with an arrow off its right edge, rather than the usual two-way exchange arrows.
 * A payout is not an exchange and it is not a transfer between two of your own accounts — it is
 * one direction, out, and the drawing should not leave that ambiguous at 16px.
 */
export const IconPayout = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 6.5h11a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H3A1.5 1.5 0 0 1 1.5 14V8A1.5 1.5 0 0 1 3 6.5Z" />
    <path d="M8.5 11h.01" />
    <path d="M18 11h4" />
    <path d="m20 9 2 2-2 2" />
  </Icon>
);

/**
 * A statement: a document with a rule above its last line.
 *
 * The rule is the whole idea. A plain page of lines is a document; a page whose final line sits
 * under a rule is a total, and a total is what a landlord opens a statement to find.
 */
export const IconStatement = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
    <path d="M8.5 11.5h7M8.5 14h7" />
    <path d="M11.5 17.5h4" />
  </Icon>
);

/**
 * One envelope holding several sheets — the aggregated reminder.
 *
 * Drawn as a stack behind a single envelope rather than as several envelopes, because that is
 * precisely the distinction the setting is about: the same number of things to say, sent once.
 */
export const IconOneEnvelope = (props: IconProps) => (
  <Icon {...props}>
    <path d="M7.5 3.5h7l3 3v4" />
    <rect x="2.5" y="9" width="19" height="11.5" rx="1.5" />
    <path d="m2.5 10.5 9.5 6 9.5-6" />
  </Icon>
);

/* ------------------------------------------------------------------------------
   Marks that arrived with a feature and earned a place in the set.

   Each of these was first drawn beside the screen that needed it, which is the right place
   for a mark whose feature may yet be reshaped. They are here now because a second screen
   wants them, and a set with two houses in it is not a set: the rail, the walkthrough and
   the data screens all draw from this file, and a duplicate drawn to a different weight is
   visible the moment the two sit in one column.
   ------------------------------------------------------------------------------ */

/**
 * An estate: a boundary with homes inside it.
 *
 * The boundary is the whole of the difference from `IconBuilding`. A developer's estate is a
 * *scheme* — a piece of ground with a wall round it and several dwellings within — and drawing it
 * as one more building would make the two indistinguishable at 18px in a list where they appear
 * one under the other.
 */
export const IconEstate = (props: IconProps) => (
  <Icon {...props}>
    <path d="M2 20.5h20" />
    <path d="M3.6 20.5V6.2A1.2 1.2 0 0 1 4.8 5h14.4a1.2 1.2 0 0 1 1.2 1.2v14.3" />
    <path d="M6.6 20.5v-5.6l3-2.4 3 2.4v5.6" />
    <path d="M14.9 20.5v-3.7l2-1.6 2 1.6v3.7" />
  </Icon>
);

/**
 * A plot: ground seen from above, with a boundary drawn across it.
 *
 * Two strokes, and it took three attempts to get there. A plain rectangle read as a card, a room or
 * a field equally well; a parcel with a surveyor's peg and a pennant on it was legible at 48px and a
 * smudge at the 17px the panel actually draws it. What survives the size is the perspective — which
 * says "ground" rather than "object" — and the line dividing it, which says the ground has been cut
 * up. That is exactly what a plot is.
 */
export const IconPlot = (props: IconProps) => (
  <Icon {...props}>
    <path d="M2.5 12 12 7l9.5 5-9.5 5-9.5-5Z" />
    <path d="m7.2 9.4 9.6 5.2" />
  </Icon>
);

/**
 * A sale: a key handed over.
 *
 * Not another document. A tenancy is already `IconContract`, and a sale drawn as a second sheet of
 * paper would say "some paperwork" rather than "this one changes hands" — precisely the distinction
 * between the two modules' central records.
 *
 * **Horizontal, not diagonal.** The first version was a ring with a diagonal shaft, which is a
 * magnifying glass at any size a list draws an icon at — it sat in the panel next to a real search
 * field and read as one. A horizontal shaft with two teeth cannot be mistaken for anything else.
 */
export const IconKeys = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="7.2" cy="12" r="4.2" />
    <path d="M11.4 12h9.4" />
    <path d="M17.2 12v3.6" />
    <path d="M20.4 12v2.6" />
  </Icon>
);

/**
 * Getting started: a flag planted at the beginning of a route.
 *
 * Read as a starting point rather than a destination because it stands on the ground line. A map
 * pin was tried first and says "a place"; a compass says "you are lost". A flag on the ground says
 * "begin here", which is what the screen is.
 */
export const IconFlag = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 21V3.8" />
    <path d="M3.6 21h4.8" />
    <path d="M6 4.6h11.4l-2.4 3.5 2.4 3.5H6" />
  </Icon>
);

/** The counterpart of `IconArrowLeft`, for a step that carries on rather than goes back. */
export const IconArrowRight = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </Icon>
);

/** Out of the product and onto a disc — the arrow points down, at a tray. */
export const IconExport = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3v11" />
    <path d="m8 10.5 4 4 4-4" />
    <path d="M4 17.5v1.5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1.5" />
  </Icon>
);

/** In from a disc — the same tray, the arrow the other way. */
export const IconImport = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 14.5V3.5" />
    <path d="m8 7.5 4-4 4 4" />
    <path d="M4 17.5v1.5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1.5" />
  </Icon>
);

/** A sheet with ruled rows — a file of columns rather than a page of prose. */
export const IconSheet = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
    <path d="M8.5 12.5h7M8.5 16h7" />
    <path d="M11.5 12.5V19" />
  </Icon>
);

/** A board with a tick on it. An inspection is a list somebody walked and signed off. */
export const IconClipboardCheck = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 4.5H7.5A1.5 1.5 0 0 0 6 6v13.5A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H15" />
    <rect x="9" y="3" width="6" height="3.2" rx="1" />
    <path d="m9.5 13.2 1.9 1.9 3.6-3.9" />
  </Icon>
);

/**
 * The padlock. One drawing, used everywhere something is shut behind a plan.
 *
 * **The shackle is closed and the body is square-shouldered on purpose.** A padlock drawn with a
 * thin, half-open shackle reads as an error state — something has gone wrong and been barred —
 * where this one has to read as "there is more here", which is a different feeling entirely. It is
 * on the same 24-grid at the same stroke as every other icon in the set, so it sits in a rail item
 * or beside a row's name without looking borrowed.
 *
 * Drawn once and never redrawn: a padlock drawn three ways is three padlocks, and a reader who
 * has to learn each of them learns instead to ignore all of them. Everything that draws one goes
 * through `Padlock` in `components/padlock.tsx`, which goes through this.
 */
export const IconLock = (props: IconProps) => (
  <Icon {...props}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    <path d="M12 14.5v2" />
  </Icon>
);

/** The same lock, open — for saying which side of a limit the records that stayed open are on. */
export const IconLockOpen = (props: IconProps) => (
  <Icon {...props}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 7.6-1.8" />
    <path d="M12 14.5v2" />
  </Icon>
);
